/* eslint-disable camelcase */
import { useEffect, useRef, useState } from 'react'

import styles from '@/pages/app/BillingOps/styles.module.scss'

import { isApiError } from '@/api'
import { commitBillingOp, previewBillingOp } from '@/api/billingOps'
import { BulkLineError } from '@/pages/app/BillingOps/bulkConvert'
import {
  BulkMoveLine,
  BulkMoveRow,
  clearBulkMove,
  countIrreversible,
  FailureBreaker,
  isUnexpectedFailure,
  loadBulkMove,
  moveRowsToCommit,
  moveRowsToCsv,
  parseCostCenterLines,
  runPaced,
  saveBulkMove,
} from '@/pages/app/BillingOps/bulkMove'
import { BillingOpsPreviewView, OPERATION_KINDS } from '@/pages/app/BillingOps/types'

// Each move starts a billing coordinator, so the pace keeps a large run from crowding live orgs off its queue.
const PACE = { perSecond: 2, maxInFlight: 16 }
const BREAKER_LIMIT = 10
const BREAKER_WINDOW = 50
const SAVE_INTERVAL_MS = 2000
const MAX_TABLE_ROWS = 500

const errorMessage = (error: unknown, fallback: string): string =>
  isApiError(error) ? error.detail || error.message : fallback

const errorStatus = (error: unknown): number | null => (isApiError(error) ? error.status : null)

const newRow = (line: BulkMoveLine): BulkMoveRow => ({
  ...line,
  status: 'pending',
  summary: null,
  irreversible: false,
  error: null,
  idempotencyRoot: crypto.randomUUID(),
})

const BulkMoveToNewBilling = () => {
  const [saved] = useState(() => loadBulkMove())
  const [text, setText] = useState(saved?.text ?? '')
  const [rows, setRowsState] = useState<BulkMoveRow[]>(saved?.rows ?? [])
  const [parseErrors, setParseErrors] = useState<BulkLineError[]>([])
  const [running, setRunning] = useState<'preview' | 'commit' | null>(null)
  const [stopReason, setStopReason] = useState<string | null>(null)
  const [copyState, setCopyState] = useState<'idle' | 'copied' | 'failed'>('idle')
  const [saveFailed, setSaveFailed] = useState(false)
  const stopRequested = useRef(false)
  // Kept in sync on every write so a run that outlives the component still saves its late completions.
  const latest = useRef({ text, rows })
  const setRows = (next: BulkMoveRow[]) => {
    latest.current = { ...latest.current, rows: next }
    setRowsState(next)
  }

  useEffect(
    () => () => {
      stopRequested.current = true
    },
    []
  )

  useEffect(() => {
    if (running === null) return
    const warn = (event: BeforeUnloadEvent) => event.preventDefault()
    window.addEventListener('beforeunload', warn)
    const save = () => setSaveFailed(!saveBulkMove(latest.current))
    const timer = window.setInterval(save, SAVE_INTERVAL_MS)
    return () => {
      window.removeEventListener('beforeunload', warn)
      window.clearInterval(timer)
      save()
    }
  }, [running])

  const updateRow = (lineNumber: number, patch: Partial<BulkMoveRow>) =>
    setRows(latest.current.rows.map((row) => (row.lineNumber === lineNumber ? { ...row, ...patch } : row)))

  const run = async (kind: 'preview' | 'commit', items: BulkMoveRow[], work: (row: BulkMoveRow) => Promise<void>) => {
    const breaker = new FailureBreaker(BREAKER_LIMIT, BREAKER_WINDOW)
    stopRequested.current = false
    setStopReason(null)
    setRunning(kind)
    try {
      await runPaced(
        items,
        PACE,
        async (row) => {
          try {
            await work(row)
            breaker.record(false)
          } catch (error) {
            breaker.record(isUnexpectedFailure(errorStatus(error)))
          }
        },
        () => {
          if (breaker.tripped) {
            setStopReason(
              `Auto-paused: ${BREAKER_LIMIT} of the last ${BREAKER_WINDOW} orgs hit unexpected errors (5xx, rate ` +
                'limit or network). Check Sentry before resuming.'
            )
            return true
          }
          if (stopRequested.current) {
            setStopReason('Paused.')
            return true
          }
          return false
        }
      )
    } finally {
      setSaveFailed(!saveBulkMove(latest.current))
      setRunning(null)
    }
  }

  const handlePreviewAll = async () => {
    const parsed = parseCostCenterLines(text)
    setParseErrors(parsed.errors)
    setCopyState('idle')
    const initial = parsed.lines.map(newRow)
    setRows(initial)
    if (initial.length === 0) return
    await previewRows(initial)
  }

  const previewRow = async (row: BulkMoveRow) => {
    try {
      const response = await previewBillingOp({
        cost_center_id: row.costCenterId,
        operation_kind: OPERATION_KINDS.MOVE_TO_NEW_BILLING,
        input: {},
      })
      const preview = response.data.preview as unknown as BillingOpsPreviewView
      updateRow(row.lineNumber, {
        status: 'previewed',
        summary: preview.customer_impact.summary,
        irreversible: preview.reversibility === 'irreversible',
        error: null,
      })
    } catch (error) {
      updateRow(row.lineNumber, {
        status: 'failed',
        error: errorMessage(error, 'Preview failed'),
      })
      throw error
    }
  }

  const previewRows = (items: BulkMoveRow[]) => run('preview', items, previewRow)

  const handleResumePreview = () => previewRows(rows.filter((row) => row.status === 'pending'))

  const handleCommitAll = async () => {
    const toCommit = moveRowsToCommit(rows)
    if (toCommit.length === 0) return
    const irreversible = countIrreversible(toCommit)
    const minutes = Math.ceil(toCommit.length / PACE.perSecond / 60)
    const confirmed = window.confirm(
      `ENGINEERING ONLY. Move ${toCommit.length} org${toCommit.length === 1 ? '' : 's'} to new billing? ` +
        `${irreversible} of them are IRREVERSIBLE (old-billing drafts moved and deleted, or seat types changed). ` +
        `The rest can be rolled back with roll_back_new_billing_move. Takes about ${minutes} min at ` +
        `${PACE.perSecond} orgs/s; keep this tab open (a reload resumes from the last saved row).`
    )
    if (!confirmed) return
    await run('commit', toCommit, commitRow)
  }

  const commitRow = async (row: BulkMoveRow) => {
    try {
      await commitBillingOp({
        cost_center_id: row.costCenterId,
        operation_kind: OPERATION_KINDS.MOVE_TO_NEW_BILLING,
        input: {},
        idempotency_root: row.idempotencyRoot,
      })
      updateRow(row.lineNumber, { status: 'committed', error: null })
    } catch (error) {
      // An unexpected error may or may not have applied, so the row stays committable under the same root.
      updateRow(row.lineNumber, {
        status: isUnexpectedFailure(errorStatus(error)) ? 'previewed' : 'failed',
        error: errorMessage(error, 'Commit failed'),
      })
      throw error
    }
  }

  const handlePreviewAndCommit = async () => {
    const parsed = rows.length > 0 ? null : parseCostCenterLines(text)
    if (parsed) setParseErrors(parsed.errors)
    const all = parsed ? parsed.lines.map(newRow) : rows
    const todo = all.filter((row) => row.status === 'pending' || row.status === 'previewed')
    if (todo.length === 0) return
    const minutes = Math.ceil(todo.length / PACE.perSecond / 60)
    const confirmed = window.confirm(
      `ENGINEERING ONLY. Preview ${todo.length} org${todo.length === 1 ? '' : 's'} and move every one that isn't ` +
        `refused to new billing? Takes about ${minutes} min at ${PACE.perSecond} orgs/s; keep this tab open (a reload ` +
        'resumes from the last saved row).'
    )
    if (!confirmed) return
    setCopyState('idle')
    if (parsed) setRows(all)
    await run('commit', todo, async (row) => {
      if (row.status === 'pending') await previewRow(row)
      await commitRow(row)
    })
  }

  const handleCopyCsv = async () => {
    try {
      await navigator.clipboard.writeText(moveRowsToCsv(rows))
      setCopyState('copied')
    } catch {
      setCopyState('failed')
    }
  }

  const handleClear = () => {
    clearBulkMove()
    latest.current = { ...latest.current, text: '' }
    setText('')
    setRows([])
    setParseErrors([])
    setStopReason(null)
  }

  const count = (status: BulkMoveRow['status']) => rows.filter((row) => row.status === status).length
  const previewedCount = count('previewed')
  const pendingCount = count('pending')
  const tableRows = [...rows.filter((row) => row.error !== null), ...rows.filter((row) => row.error === null)].slice(
    0,
    MAX_TABLE_ROWS
  )

  return (
    <div className={styles.panel}>
      <div className={styles.section}>
        <h2 className={styles.sectionTitle}>Bulk: move to new billing</h2>
        <div className={styles.errorBanner}>
          <strong>Engineering only. Support: do not run this.</strong>
        </div>
        <p className={styles.helperText}>
          One cost center per line; the first id on each line is used, so rows from a CSV paste as-is. Each line
          previews and commits the same single-org operation, {PACE.perSecond} orgs per second. Refused orgs show every
          reason and are skipped; a failed line doesn&apos;t stop the rest, and re-running is safe. The run pauses
          itself after {BREAKER_LIMIT} unexpected errors in {BREAKER_WINDOW} orgs. Progress is saved in this browser, so
          a reload picks up where it stopped.
        </p>
        <textarea
          className={styles.input}
          rows={8}
          value={text}
          onChange={(e) => {
            latest.current = { ...latest.current, text: e.target.value }
            setText(e.target.value)
            setRows([])
            clearBulkMove()
            setParseErrors([])
            setStopReason(null)
          }}
          placeholder="00000000-0000-0000-0000-000000000000"
          disabled={running !== null}
          aria-label="Cost centers to move to new billing"
        />
        {parseErrors.length > 0 && (
          <div className={styles.errorBanner}>
            <ul className={styles.list}>
              {parseErrors.map((e) => (
                <li key={e.lineNumber}>
                  Line {e.lineNumber}: {e.message}
                </li>
              ))}
            </ul>
          </div>
        )}
        {stopReason && <div className={styles.errorBanner}>{stopReason}</div>}
        {saveFailed && (
          <div className={styles.errorBanner}>
            Progress is too large to save in this browser, so a reload won&apos;t resume it. Copy the CSV before
            leaving.
          </div>
        )}
        <div className={styles.actionRow}>
          <button
            type="button"
            className={styles.opItem}
            style={{ width: 'auto' }}
            onClick={handlePreviewAll}
            disabled={running !== null || text.trim() === ''}
          >
            <span className={styles.opItemKind}>{running === 'preview' ? 'Previewing…' : 'Preview all'}</span>
          </button>
          {running === null && pendingCount > 0 && (
            <button type="button" className={styles.opItem} style={{ width: 'auto' }} onClick={handleResumePreview}>
              <span className={styles.opItemKind}>Resume preview ({pendingCount} left)</span>
            </button>
          )}
          <button
            type="button"
            className={styles.opItem}
            style={{ width: 'auto' }}
            onClick={handleCommitAll}
            disabled={running !== null || previewedCount === 0}
          >
            <span className={styles.opItemKind}>
              {running === 'commit' ? 'Committing…' : `Commit ${previewedCount} previewed`}
            </span>
          </button>
          <button
            type="button"
            className={styles.opItem}
            style={{ width: 'auto' }}
            onClick={handlePreviewAndCommit}
            disabled={running !== null || (text.trim() === '' && rows.length === 0)}
          >
            <span className={styles.opItemKind}>
              {pendingCount + previewedCount > 0 ? 'Preview and commit remaining' : 'Preview and commit'}
            </span>
          </button>
          {running !== null && (
            <button
              type="button"
              className={styles.opItem}
              style={{ width: 'auto' }}
              onClick={() => {
                stopRequested.current = true
              }}
            >
              <span className={styles.opItemKind}>Pause</span>
            </button>
          )}
          <button
            type="button"
            className={styles.opItem}
            style={{ width: 'auto' }}
            onClick={handleCopyCsv}
            disabled={running !== null || rows.length === 0}
          >
            <span className={styles.opItemKind}>
              {copyState === 'copied'
                ? 'Copied'
                : copyState === 'failed'
                  ? 'Copy failed, try again'
                  : 'Copy results as CSV'}
            </span>
          </button>
          <button
            type="button"
            className={styles.opItem}
            style={{ width: 'auto' }}
            onClick={handleClear}
            disabled={running !== null || (rows.length === 0 && text === '')}
          >
            <span className={styles.opItemKind}>Clear saved run</span>
          </button>
        </div>
        {rows.length > 0 && (
          <p className={styles.helperText}>
            {rows.length} orgs: {pendingCount} pending, {previewedCount} previewed, {count('committed')} committed,{' '}
            {count('failed')} refused or failed.
          </p>
        )}
      </div>

      {rows.length > 0 && (
        <div className={styles.section}>
          <table className={styles.diffTable}>
            <caption className={styles.helperText}>
              Bulk move results
              {rows.length > MAX_TABLE_ROWS
                ? ` (failures first, showing ${MAX_TABLE_ROWS} of ${rows.length}; copy the CSV for all)`
                : ''}
            </caption>
            <thead>
              <tr>
                <th>Line</th>
                <th>Cost center</th>
                <th>Status</th>
                <th>Details</th>
              </tr>
            </thead>
            <tbody>
              {tableRows.map((row) => (
                <tr key={row.lineNumber}>
                  <td>{row.lineNumber}</td>
                  <td>{row.costCenterId}</td>
                  <td>{row.status}</td>
                  <td>{row.error ?? row.summary ?? ''}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}

export default BulkMoveToNewBilling
